import React, { useState, useEffect, useRef } from 'react';
import { Flex, Button, Row, Col, Select } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useDraggable } from "react-use-draggable-scroll";
import BlockLoading from "../../../components/LoadingComponents/BlockLoading";
import IntlMessage from "../../../components/IntlMessage/IntlMessage";
import EmissionString from "./EmissionString";
import cl from "../../Data/style.module.css";

const DataTable = ({trends, loading}) => {
    const timestamps = [...new Set(trends.map(d => d.timestamp))].sort();
    const names = [...new Set(trends.map(d => d.name))];

    const valueDict = {};
    trends.forEach(d => {
        if (!valueDict[d.name]) valueDict[d.name] = {};
        valueDict[d.name][d.timestamp] = d.value;
    });

    const [columnsPerPage, setColumnsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const pageCount = Math.ceil(timestamps.length / columnsPerPage);
    const currentTimestamps = timestamps.slice(currentPage * columnsPerPage, (currentPage + 1) * columnsPerPage);

    const ref = useRef();
    const { events } = useDraggable(ref);

    useEffect(() => {
        setCurrentPage(0)
    }, [trends]);

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    };

    const rows = names.map((name, rowIndex) => (
        <tr key={name} className={cl.row} >
            <td className={cl.nameCell} style={{fontWeight: 700}}>
                <EmissionString emission={{name}}/>
            </td>
            {currentTimestamps.map(timestamp => {
                const value = valueDict[name][timestamp];
                return (
                    <td key={timestamp} style={{color: "black"}} className={rowIndex % 2 === 0 ? cl.evenRow : cl.oddRow}>
                        {value !== undefined ? value.toFixed(2) : ''}
                    </td>
                );
            })}
        </tr>
    ));

    const handlePrevPage = () => setCurrentPage(Math.max(currentPage - 1, 0));
    const handleNextPage = () => setCurrentPage(Math.min(currentPage + 1, pageCount - 1));
    const handleColumnsPerPageChange = (value) => {
        setColumnsPerPage(value);
        setCurrentPage(0);
    };

    return (
        <Flex style={{width: "100%"}} vertical gap={10}>
            <Row className={cl.pagination} align="middle" justify="center" gutter={[10, 0]}>
                <Col>
                    <Button onClick={handlePrevPage} disabled={currentPage === 0}>
                        <LeftOutlined/>
                    </Button>
                </Col>
                <Col>
                    <span><IntlMessage id={'dataPage.page'}/> {currentPage + 1} / {pageCount}</span>
                </Col>
                <Col>
                    <Button onClick={handleNextPage} disabled={currentPage === pageCount - 1}>
                        <RightOutlined/>
                    </Button>
                </Col>
                <Col>
                    <Flex align={"center"} justify={"center"}>
                        <Select defaultValue={columnsPerPage} onChange={handleColumnsPerPageChange}>
                            <Select.Option value={10}>10</Select.Option>
                            <Select.Option value={20}>20</Select.Option>
                            <Select.Option value={30}>30</Select.Option>
                        </Select>
                    </Flex>
                </Col>
            </Row>
            <Flex {...events} ref={ref} style={{width: "100%", overflow: "scroll", position: "relative"}}>
                <BlockLoading isLoading={loading}/>
                <table className={cl.dataTable}>
                    <thead>
                    <tr>
                        <th style={{fontWeight: 700}}>
                            <IntlMessage id={'dataPage.name/timestamp'}/>
                        </th>
                        {currentTimestamps.map(timestamp => (
                            <th key={timestamp}>{formatTimestamp(timestamp)}</th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {rows}
                    </tbody>
                </table>
            </Flex>
        </Flex>
    );
}

export default DataTable;